import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getProjectSla, updateProjectSla } from '../../../../../helpers/project-helper';

const SLA_TYPES = ['INCIDENT', 'REQUEST', 'PROBLEM', 'CHANGE', 'TASK'];

const TYPE_LABELS = {
    INCIDENT: 'Incidente',
    REQUEST: 'Solicitud',
    PROBLEM: 'Problema',
    CHANGE: 'Cambio',
    TASK: 'Tarea',
};

const mapSlaResponseToForm = (sla = {}) => {
    return SLA_TYPES.reduce((accumulator, type) => {
        const resolutionHours = Number(sla?.[type]?.resolutionHours);

        return {
            ...accumulator,
            [type]: {
                resolutionHours: Number.isNaN(resolutionHours) ? 0 : resolutionHours,
            },
        };
    }, {});
};

export const ProjectSlaTabContent = ({ projectId }) => {
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [slaForm, setSlaForm] = React.useState({});

    React.useEffect(() => {
        const loadSla = async () => {
            if (!token || !projectId) {
                setError('No se pudo obtener la configuración SLA del proyecto.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getProjectSla(token, projectId);
            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener la configuración SLA del proyecto.');
                setIsLoading(false);
                return;
            }

            setSlaForm(mapSlaResponseToForm(resp.data || {}));
            setIsLoading(false);
        };

        loadSla();
    }, [token, projectId]);

    const handleSlaHoursChange = (type, value) => {
        setSlaForm((prev) => ({
            ...prev,
            [type]: {
                resolutionHours: value,
            },
        }));
    };

    const handleSaveSla = async () => {
        if (!token || !projectId || isSaving) return;

        setIsSaving(true);
        setError('');
        const savingToastId = toast.loading('Guardando SLA del proyecto...');

        const payload = SLA_TYPES.reduce((accumulator, type) => {
            const parsedValue = Number(slaForm?.[type]?.resolutionHours);
            const normalizedValue = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
            const safeValue = type === 'INCIDENT' && normalizedValue < 1 ? 1 : normalizedValue;

            return {
                ...accumulator,
                [type]: {
                    resolutionHours: safeValue,
                },
            };
        }, {});

        const resp = await updateProjectSla(token, projectId, payload);
        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar la configuración SLA del proyecto.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSaving(false);
            return;
        }

        setSlaForm(mapSlaResponseToForm(resp.data || {}));
        toast.update(savingToastId, {
            render: resp?.message || 'SLA actualizado correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSaving(false);
    };

    return (
        <div className="profile-details mb-1">
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">SLA por tipo de ticket</h5>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveSla}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? 'Guardando...' : 'Guardar SLA'}
                </button>
            </div>

            {isLoading && <div className="py-3 text-muted">Cargando SLA...</div>}

            {!isLoading && error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {!isLoading && !error && (
                <div className="table-responsive border border-gray-3 rounded-2">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Tipo</th>
                                <th className="text-uppercase fs-11 fw-semibold py-3">Horas de resolución</th>
                            </tr>
                        </thead>
                        <tbody>
                            {SLA_TYPES.map((type) => (
                                <tr key={type}>
                                    <td className="py-2 fw-semibold">{TYPE_LABELS[type] || type}</td>
                                    <td className="py-2" style={{ maxWidth: '220px' }}>
                                        <div className="input-group input-group-sm">
                                            <input
                                                type="number"
                                                min={type === 'INCIDENT' ? '1' : '0'}
                                                step="0.5"
                                                className="form-control"
                                                value={slaForm?.[type]?.resolutionHours ?? 0}
                                                onChange={(event) => handleSlaHoursChange(type, event.target.value)}
                                            />
                                            <span className="input-group-text">hrs</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
